/**
 * Self-hosted voice client (STT half) — talks to the founder's own /api/stt
 * (backed by voice-inference/, never a third-party vendor). Every function
 * here degrades to `null`/`false` on any failure — callers fall back to the
 * existing browser Web Speech path. Never throws, never surfaces a raw error.
 *
 * The TTS half lives directly in lib/tts.ts (ttsNeural()) — that file already
 * owns the self-host-then-browser-fallback contract for "Listen", including
 * the richer onboarding-language detection and autoplay-failure handling
 * from the voice-tts-selfhost branch; no need to duplicate it here.
 *
 * See docs/VOICE-0-rule-change.md for why this is allowed to live in the
 * main composer (privacy-first, self-hosted only — no vendor API).
 */

const REQUEST_TIMEOUT_MS = 6000;

export interface SelfHostedSTTResult {
  text: string;
  language: string;
}

/**
 * Try self-hosted STT on a recorded audio blob. Returns the transcript on
 * success, or null on any failure — callers must fall back to the browser
 * SpeechRecognition path on null.
 */
export async function trySelfHostedSTT(
  audioBlob: Blob,
  language: "hi" | "en" | "auto" = "auto",
): Promise<SelfHostedSTTResult | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS * 3); // transcription is slower than TTS
  try {
    const form = new FormData();
    form.append("file", audioBlob, "speech.webm");
    form.append("language", language);
    const res = await fetch("/api/stt", { method: "POST", body: form, signal: controller.signal });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok || typeof data.text !== "string") return null;
    return { text: data.text, language: data.language };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Whether this browser can record audio at all (needed before attempting self-hosted STT). */
export function canRecordAudio(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}

export interface AudioRecording {
  stop: () => Promise<Blob>;
}

/**
 * Start recording microphone audio. Resolves once recording has actually
 * started (permission granted, stream open); throws if permission is denied
 * or no mic is available — callers should catch and fall back to browser
 * SpeechRecognition, which has its own permission prompt.
 */
export async function startRecording(): Promise<AudioRecording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream);
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.start();

  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          resolve(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
        };
        recorder.stop();
      }),
  };
}
